import React, { FC } from 'react';
import DraggableImage from './DraggableImage';
import { ImageElementType } from '../../redux/TabTile/TabStateInterfaces';
import { useSelector, useDispatch } from 'react-redux';
import {
  updateImagePosition,
  updateImageSize,
  removeImageElement,
  setSelectedImageId,
  setSelectedRichTextId,
} from '../../redux/TabTile/TabActions';
import { RootState } from '../../redux';

interface ImageProps {
  softUI: boolean;
  backgroundColor: string;
  currentTabId: number;
  colorScheme: string;
  sidebarOpen?: boolean;
  setSidebarOpen?: (val: boolean) => void;
  dashMode: string;
  dashboardHeight?: number;
  dashboardWidth?: number;
  showObjectsSidebar?: boolean;
  setShowListofTileMenu?: (val: boolean) => void;
  setLayerMode?: React.Dispatch<React.SetStateAction<'tiles' | 'objects'>>;
  setDashboardResizeColumn?: (val: boolean) => void;
  setShowDashboardFilter?: (val: boolean) => void;
  setSelectedObject?: (object: string) => void;
  setShowDashboardFilterEdit?: (val: boolean) => void;
  setShowObjectsSidebar?: React.Dispatch<React.SetStateAction<boolean>>;
}

const Image: FC<ImageProps> = ({
  softUI,
  backgroundColor,
  colorScheme = 'default',
  setSidebarOpen,
  currentTabId,
  setShowListofTileMenu,
  setLayerMode,
  setDashboardResizeColumn,
  dashMode = 'Edit',
  setShowDashboardFilter,
  setSelectedObject,
  setShowObjectsSidebar,
  dashboardHeight,
  setShowDashboardFilterEdit,
  dashboardWidth,
}) => {
  const dispatch = useDispatch();
  const tab = useSelector((state: RootState) => state.tabState.tabs[currentTabId]);
  const tabTileProps = useSelector((state: RootState) => state.tabTileProps);
  const { imageElements, selectedImageId } = tab.dashboardObjects;

  const handleImageClick = (id: string) => {
    if (selectedImageId === id && tabTileProps.showObjectsSidebar) return;
    dispatch(setSelectedRichTextId(currentTabId, null));
    dispatch(setSelectedImageId(currentTabId, id));
    setSidebarOpen?.(true);
    setSelectedObject?.('image');
    setShowObjectsSidebar?.(false);
    setShowListofTileMenu?.(true);
    setLayerMode?.('objects');
    setDashboardResizeColumn?.(false);
    setShowDashboardFilter?.(false);
    setShowDashboardFilterEdit?.(false);
  };

  return (
    <>
      {imageElements.map((img: ImageElementType) => {
        if (img.type === 'icon-button') return null;

        return (
          <DraggableImage
            key={img.id}
            id={img.id}
            imageAlpha={img.imageAlpha ?? 100}
            src={img.src || ''}
            x={img.x}
            y={img.y}
            width={img.width}
            height={img.height}
            onDragStop={(e: any, d: any, gridSizeX: number, gridSizeY: number) => {
              const newX = Math.round(d.x / gridSizeX);
              const newY = Math.round(d.y / gridSizeY);
              dispatch(updateImagePosition(currentTabId, img.id, newX, newY));
            }}
            onResizeStop={(ref: any, position: any, gridSizeX: number, gridSizeY: number) => {
              const newWidth = parseInt(ref.style.width);
              const newHeight = parseInt(ref.style.height);
              dispatch(
                updateImageSize(
                  currentTabId,
                  img.id,
                  newWidth / gridSizeX,
                  newHeight / gridSizeY,
                  position.x / gridSizeX,
                  position.y / gridSizeY,
                ),
              );
            }}
            onDelete={() => {
              dispatch(removeImageElement(currentTabId, img.id));
              if (selectedImageId === img.id) {
                dispatch(setSelectedImageId(currentTabId, null));
              }
            }}
            onClick={() => handleImageClick(img.id)}
            softUI={softUI}
            backgroundColor={img.backgroundColor || backgroundColor}
            backgroundAlpha={img.backgroundAlpha ?? 50}
            borderColor={img.borderColor || 'transparent'}
            borderWidth={img.borderWidth ?? 0}
            borderRadius={img.borderRadius ?? 12}
            scaling={img.scaling || 'normal'}
            colorScheme={colorScheme}
            dashMode={dashMode}
            dashboardHeight={dashboardHeight}
            dashboardWidth={dashboardWidth}
          />
        );
      })}
    </>
  );
};

export default Image;
